import type { MetaHTMLAttributes } from 'react'
import { useParams, useSearchParams } from 'react-router'
import { useSelector } from 'react-redux'
import Immutable from 'immutable'

import { DocumentMeta } from '../components/DocumentMeta'
import { PageOrCategoryContext } from './Category'
import { createMultiLanguageLinks } from './utils/createMultiLanguageLinks'
import { setPageAsync } from '../store/actions'
import Theme from '../components/Theme'

export default function Page() {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const viewError = useSelector<State, ImmutableMap>((state) => state.getIn(['view', 'error']))
  const shop = useSelector<State, ImmutableMap>((state) => state.get('shop'))
  const page = useSelector<State, ImmutableMap>((state) => state.get('pages').get(params.pageSlug || 'home'))

  if (viewError || !page) {
    return (
      <>
        <DocumentMeta />
        <Theme withLayout error={viewError} currentView="page" />
      </>
    )
  }

  const isBeyondShop = Boolean(shop.get('beyond'))
  const isBeyondHomepage = isBeyondShop && page.get('slug') === 'home'

  // Use the merchant-provided title if set, otherwise use the page title.
  // Append the shop title.
  //
  // If it is a Beyond shop, the page could be the home page.
  // In this case, use the merchant-provided title if set, otherwise the shop
  // title with a fallback to the page title.
  const title = isBeyondHomepage
    ? page.get('titleTag') || shop.get('title') || page.get('title')
    : page.get('titleTag') || [page.get('title'), shop.get('title')].filter(Boolean).join(' - ')

  const meta: MetaHTMLAttributes<HTMLMetaElement>[] = []
  if (page.get('metaDescription')) {
    meta.push({ name: 'description', content: page.get('metaDescription') })
  }

  // For category content
  if (searchParams.get('sort') || searchParams.get('page')) {
    meta.push({
      name: 'robots',
      content: 'noindex, follow',
    })
  }

  return (
    <PageOrCategoryContext.Provider value={page}>
      <DocumentMeta
        title={title}
        meta={meta}
        links={createMultiLanguageLinks(
          'blank',
          isBeyondHomepage ? '' : page.get('slugs', Immutable.List()).toJS(),
          shop,
        )}
      />
      <Theme withLayout currentView="page">
        {(renderView, props) =>
          renderView('Page', {
            ...props,
            page: page.toJS(),
          })
        }
      </Theme>
    </PageOrCategoryContext.Provider>
  )
}

Page.storeUpdate = (props: RouteComponentProps<'pageSlug'> & { isHomepage?: boolean }, state: State) => {
  return props.location.action === 'PUSH' || !state.getIn(['pages', props.params.pageSlug])
    ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      [setPageAsync(props.params.pageSlug!, props.location)]
    : []
}

Page.contentCreationDisabled = () => false
